/**
 * Quiz - Alpine.js component
 *
 * Renders the interactive quiz (stepper, validation) and submits the chosen answers
 * to the server-side scoring endpoint (/quiz-api/submit). Scoring, the answer key and
 * the per-question feedback live on the server only — this component never sees them
 * until the response comes back, and reveals the matching (pre-rendered) result panel
 * inline. Configuration and all user-facing strings are read from data-* attributes so
 * no localized text lives in JS.
 *
 * NOTE: this component queries the quiz DOM via `this.$root` (the x-data root element), NOT
 * `this.$el`. When a method is invoked from an event on a child element — e.g. next() from the
 * "Next" button's @click — Alpine's `$el` resolves to that child (the button), so
 * `$el.querySelectorAll('.js-quiz-question')` would find nothing and validation would wrongly
 * fail. `$root` always points at the quiz container.
 */
document.addEventListener('alpine:init', () => {
    Alpine.data('quiz', () => ({
        endpoint: '',
        content: 0,
        mode: 'selective',
        single: false,
        currentStep: 0,
        totalSteps: 0,
        finished: false,
        loading: false,
        error: false,
        errorMsg: '',
        resultId: 0,
        score: 0,
        total: 0,
        review: [],
        messages: { choose: '', answerAll: '', error: '' },

        init() {
            const d = this.$root.dataset;
            this.endpoint = d.quizEndpoint || '';
            this.content = parseInt(d.quizContent || '0', 10);
            this.mode = d.quizMode || 'selective';
            this.single = d.quizSingle === '1';
            this.messages.choose = d.msgChoose || '';
            this.messages.answerAll = d.msgAnswerAll || '';
            this.messages.error = d.msgError || '';
            this.totalSteps = this.$root.querySelectorAll('.js-quiz-question').length;
        },

        get isLastStep() {
            return this.currentStep >= this.totalSteps - 1;
        },

        clearError() {
            this.error = false;
            this.errorMsg = '';
        },

        showError(message) {
            this.error = true;
            this.errorMsg = message;
        },

        /** Is the currently visible question (single mode) answered? */
        stepAnswered(index) {
            const fieldsets = this.$root.querySelectorAll('.js-quiz-question');
            const fieldset = fieldsets[index];
            return !!(fieldset && fieldset.querySelector('input[type="radio"]:checked'));
        },

        next() {
            if (!this.stepAnswered(this.currentStep)) {
                this.showError(this.messages.choose);
                return;
            }
            this.clearError();
            if (!this.isLastStep) {
                this.currentStep++;
            }
        },

        /** Collect { questionUid: answerUid } from the checked radios. */
        collect() {
            const answers = {};
            this.$root.querySelectorAll('.js-quiz-question').forEach((fieldset) => {
                const checked = fieldset.querySelector('input[type="radio"]:checked');
                if (checked) {
                    answers[fieldset.dataset.question] = checked.value;
                }
            });
            return answers;
        },

        async submit() {
            const answers = this.collect();
            if (Object.keys(answers).length < this.totalSteps) {
                this.showError(this.messages.answerAll);
                return;
            }
            this.clearError();
            this.loading = true;
            try {
                const response = await fetch(this.endpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: JSON.stringify({ content: this.content, answers }),
                });
                if (!response.ok) {
                    throw new Error('HTTP ' + response.status);
                }
                const data = await response.json();
                this.resultId = parseInt(data.resultId || 0, 10);
                this.score = parseInt(data.score || 0, 10);
                this.total = parseInt(data.total || 0, 10);
                this.review = Array.isArray(data.review) ? data.review : [];
                this.finished = true;
                this.$nextTick(() => this.$root.scrollIntoView({ behavior: 'smooth', block: 'start' }));
            } catch (e) {
                this.showError(this.messages.error);
                console.error('[Quiz]', e);
            } finally {
                this.loading = false;
            }
        },

        restart() {
            this.$root.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
                input.checked = false;
            });
            this.currentStep = 0;
            this.finished = false;
            this.resultId = 0;
            this.score = 0;
            this.total = 0;
            this.review = [];
            this.clearError();
            this.$nextTick(() => this.$root.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        },
    }));
});
